import { useEffect, useRef } from "react";
import { Loader } from "@mantine/core";

import styles from "./FilesHub.module.scss";

/**
 * Walks up to the element that actually scrolls, so the observer measures against that box rather
 * than the viewport. The hub's grids live inside `.fileManagerBody` (`overflow: auto; flex: 1`),
 * which clips them: with the viewport as root, `rootMargin` expands the wrong rectangle and the
 * pre-load margin does nothing. Returns null when nothing scrolls yet -- an unfilled first page,
 * or jsdom, where every element reports zero height -- and the observer then falls back to the
 * viewport, which is correct for a short list.
 */
function findScrollParent(node: HTMLElement | null): HTMLElement | null {
  let el = node?.parentElement ?? null;
  while (el) {
    const overflowY = getComputedStyle(el).overflowY;
    if ((overflowY === "auto" || overflowY === "scroll") && el.scrollHeight > el.clientHeight) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

type Props = {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  /** How far ahead of the sentinel to start loading. */
  rootMargin?: string;
};

/**
 * End-of-list marker that asks for the next page as it comes into view. Render it directly after
 * the grid it belongs to.
 */
export function InfiniteScrollSentinel({
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  rootMargin = "300px",
}: Props) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Read through refs inside the observer so a page landing (which flips isFetchingNextPage) does
  // not tear down and rebuild the observer -- reconnecting mid-scroll drops the intersection that
  // was about to fire and the list stalls until the user scrolls again.
  const stateRef = useRef({ hasNextPage, isFetchingNextPage, fetchNextPage });
  stateRef.current = { hasNextPage, isFetchingNextPage, fetchNextPage };

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        const {
          hasNextPage: more,
          isFetchingNextPage: busy,
          fetchNextPage: load,
        } = stateRef.current;
        if (more && !busy) load();
      },
      { root: findScrollParent(node), rootMargin },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [rootMargin]);

  if (!hasNextPage && !isFetchingNextPage) return null;

  return (
    <div ref={sentinelRef} className={styles.infiniteScrollSentinel} data-testid="load-more">
      {isFetchingNextPage && <Loader size="sm" />}
    </div>
  );
}
